import * as React from 'react'
import { Text } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { formatTimeInMinutes } from '../../../../helpers/numberHelper'
import useCurrencies from '../../../../hooks/useCurrencies'
import { useSettings } from '../../../../hooks/useSettings'
import MoneyText from '../../../../components/Text/Currency/MoneyText'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { CurrencyAmount, DailyTotalDto, sumMonth } from './timeCalendarHelper'

type Props = {
    // The period the totals are about, e.g. a date or a month.
    title: string
    // The days the totals are built from: a single day or a whole month.
    days: DailyTotalDto[]
    // Only shown where it applies, so not for a single day and not for combined users.
    normInMinutes?: number
}

/**
 * The hours split by billability and the turnover split into invoiced, still to be invoiced and the total.
 * Shared by the header info label and the day tooltips. Laid out as a grid like the invoice overview's turnover
 * tooltip: label on the left, currency symbol in the middle, the value right aligned.
 */
export default function CalendarTotals(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate } = useTranslation()
    const { getCurrency } = useCurrencies()
    const { settings } = useSettings()

    const totals = sumMonth(props.days)
    // Nothing written means no currency to report either; fall back to the office default so the zero still
    // reads as an amount.
    const currencies = totals.currencies.length ? totals.currencies : [settings?.fileScaffold?.currency || 'EUR']

    const hoursRow = (labelKey: string, minutes: number, bold?: boolean) => (
        <>
            <Text weight={bold ? 'semibold' : 'regular'}>{translate(labelKey)}</Text>
            <Text />
            <Text align={'end'} weight={bold ? 'semibold' : 'regular'}>
                {formatTimeInMinutes(minutes)}
            </Text>
        </>
    )

    // Currencies are never converted, so every currency written in the period gets its own line. A currency
    // without an amount on this line still shows a zero, so the lines stay comparable.
    const amountRows = (labelKey: string, amounts: CurrencyAmount[], bold?: boolean) =>
        currencies.map((currency, index) => (
            <React.Fragment key={currency}>
                <Text weight={bold ? 'semibold' : 'regular'}>{index === 0 ? translate(labelKey) : ''}</Text>
                <Text>{getCurrency(currency)?.symbol}</Text>
                <MoneyText
                    amount={amounts.find(entry => entry.currency === currency)?.amount ?? 0}
                    alpha={currency}
                    hideCurrency
                    textProps={{ size: 300, weight: bold ? 'semibold' : 'regular' }}
                    align={'right'}
                />
            </React.Fragment>
        ))

    return (
        <div className={styles.infoContent}>
            <Text weight={'semibold'} className={styles.infoFullRow}>
                {props.title}
            </Text>
            <div className={styles.infoSpacer} />

            <Text weight={'semibold'} className={styles.infoFullRow}>
                {translate('time.calendar.info.hours')}
            </Text>
            <div className={styles.infoDivider} />
            {!!props.normInMinutes && hoursRow('time.calendar.info.norm', props.normInMinutes)}
            {hoursRow('time.calendar.info.billable', totals.minutesBillable)}
            {hoursRow('time.calendar.info.offered', totals.minutesOffered)}
            {hoursRow('time.calendar.info.notbillable', totals.minutesNotBillable)}
            {hoursRow('time.calendar.info.totalhours', totals.minutes, true)}

            <div className={styles.infoDivider} />
            <Text weight={'semibold'} className={styles.infoFullRow}>
                {translate('time.calendar.info.turnover')}
            </Text>
            {amountRows('time.calendar.info.invoiced', totals.invoiced)}
            {amountRows('time.calendar.info.outstanding', totals.outstanding)}

            <div className={styles.infoDivider} />
            {amountRows('time.calendar.info.expected', totals.expected, true)}
        </div>
    )
}
